import axios from 'axios';

// Get backend URL with fallback and validation
const getBackendUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL;
  
  if (!url || url === 'undefined') {
    console.error('❌ REACT_APP_BACKEND_URL is not configured. Please set it in your .env file.');
    // Return empty string to prevent undefined in URLs
    return '';
  }
  
  return url;
};

const BACKEND_URL = getBackendUrl();
const API_URL = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Check if backend URL is configured
  if (!BACKEND_URL) {
    console.error('❌ API request failed: REACT_APP_BACKEND_URL is not configured');
  }
  
  const token = localStorage.getItem('dockhta_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('dockhta_token');
      localStorage.removeItem('dockhta_user');
      // Don't redirect if we're on auth callback
      if (!window.location.hash?.includes('session_id=')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  resendOTP: (data) => api.post('/auth/resend-otp', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  googleSession: (sessionId) => api.post(`/auth/google/session?session_id=${sessionId}`),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

// Professionals API
export const professionalsAPI = {
  search: (params) => api.get('/professionals/search', { params }),
  getDetail: (id) => api.get(`/professionals/${id}`),
  getPublicProfile: (id) => api.get(`/professionals/${id}/public`),
  createProfile: (data) => api.post('/professionals/profile', data),
  updateProfile: (data) => api.put('/professionals/profile', data),
  getProfile: () => api.get('/professionals/profile'),
  getMyProfile: () => api.get('/professionals/profile'),
  getStats: () => api.get('/professionals/dashboard/stats'),
  // Availability
  updateConsultationSlots: (slots) => api.put('/professionals/availability/consultation', slots),
  updateVideoSlots: (slots) => api.put('/professionals/availability/video', slots),
  updateHomeVisitSettings: (areas, radius) => api.put(`/professionals/availability/home-visit?radius_km=${radius}`, areas),
  getAvailability: (professionalId, serviceType) => api.get(`/professionals/availability/${professionalId}?service_type=${serviceType}`),
  // Photo uploads
  uploadProfilePhoto: (formData) => api.post('/professionals/profile/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadCoverPhoto: (formData) => api.post('/professionals/profile/cover', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Users API
export const usersAPI = {
  updateProfile: (data) => api.put('/users/profile', data),
  getProfile: (userId) => api.get(`/users/${userId}/profile`),
};

// Appointments API
export const appointmentsAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: (params) => api.get('/appointments', { params }),
  getDetail: (id) => api.get(`/appointments/${id}`),
  updateStatus: (id, status) => api.put(`/appointments/${id}/status?status=${status}`),
  completeWithSummary: (id, summary) => api.put(`/appointments/${id}/complete`, summary),
  getSummary: (id) => api.get(`/appointments/${id}/summary`),
  getBookedSlots: (professionalId, date) => api.get(`/appointments/booked-slots/${professionalId}?date=${date}`),
};

// Professional Availability API
export const availabilityAPI = {
  get: () => api.get('/professional/availability'),
  update: (data) => api.put('/professional/availability', data),
  getAvailableSlots: (professionalId, date) => api.get(`/professional/${professionalId}/available-slots?date=${date}`),
};

// Consultation Documents API
export const consultationDocumentsAPI = {
  upload: (formData, title) => api.post(`/consultation-documents/upload?title=${encodeURIComponent(title)}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/consultation-documents/${id}`),
};

// Patient Consultations API
export const patientConsultationsAPI = {
  getAll: () => api.get('/patient/consultations'),
  exportPDF: (summaryId) => api.get(`/patient/consultations/${summaryId}/pdf`, { responseType: 'blob' }),
};

// Family Members API
export const familyMembersAPI = {
  getAll: () => api.get('/family-members'),
  getOne: (id) => api.get(`/family-members/${id}`),
  create: (data) => api.post('/family-members', data),
  update: (id, data) => api.put(`/family-members/${id}`, data),
  delete: (id) => api.delete(`/family-members/${id}`),
  getMedicalRecords: (id) => api.get(`/family-members/${id}/medical-records`),
  getAppointments: (id) => api.get(`/family-members/${id}/appointments`),
};

// Healthcare Facilities API
export const facilitiesAPI = {
  getAll: (params) => api.get('/facilities', { params }),
  getOne: (id) => api.get(`/facilities/${id}`),
  create: (data) => api.post('/facilities', data),
  update: (id, data) => api.put(`/facilities/${id}`, data),
  delete: (id) => api.delete(`/facilities/${id}`),
  addReview: (id, review) => api.post(`/facilities/${id}/reviews`, review),
  getCities: () => api.get('/facilities/cities/list'),
  getTypes: () => api.get('/facilities/types/list'),
};

// Home Care API
export const homeCareAPI = {
  create: (data) => api.post('/home-care', data),
  getAll: () => api.get('/home-care'),
  accept: (id) => api.put(`/home-care/${id}/accept`),
  confirm: (id, date, time) => api.put(`/home-care/${id}/confirm?confirmed_date=${date}&confirmed_time=${time}`),
  start: (id) => api.put(`/home-care/${id}/start`),
  complete: (id) => api.put(`/home-care/${id}/complete`),
};

// Medical Records API
export const medicalRecordsAPI = {
  create: (data) => api.post('/medical-records', data),
  getAll: (params) => api.get('/medical-records', { params }),
};

// Payments API
export const paymentsAPI = {
  process: (data) => api.post('/payments', data),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
};

// Agent API
export const agentAPI = {
  createPatient: (data) => api.post('/agent/create-patient', data),
  getPatients: () => api.get('/agent/patients'),
  bookAppointment: (patientId, data) => api.post(`/agent/book-appointment?patient_id=${patientId}`, data),
};

// Admin API
export const adminAPI = {
  // Stats & Analytics
  getStats: () => api.get('/admin/stats'),
  getRealtimeKPIs: () => api.get('/admin/stats/realtime'),
  getChartData: (period = 'week') => api.get(`/admin/stats/charts?period=${period}`),
  
  // Professional Validation
  getPendingProfessionals: () => api.get('/admin/pending-professionals'),
  validateProfessional: (id, approved) => api.put(`/admin/validate-professional/${id}?approved=${approved}`),
  
  // User Management
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetail: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  suspendUser: (id, suspended) => api.put(`/admin/users/${id}/suspend?suspended=${suspended}`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  
  // Appointments & Home Care
  getAllAppointments: () => api.get('/admin/appointments'),
  getAllHomeCare: () => api.get('/admin/home-care'),
  
  // Activity Logs
  getLogs: (params) => api.get('/admin/logs', { params }),
  
  // Data Export (Advanced)
  exportUsers: (role) => api.get(`/admin/export/users${role ? `?role=${role}` : ''}`),
  exportAppointments: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.status) params.append('status', filters.status);
    if (filters.professionalId) params.append('professional_id', filters.professionalId);
    return api.get(`/admin/export/appointments?${params.toString()}`);
  },
  exportTransactions: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.status) params.append('status', filters.status);
    return api.get(`/admin/export/transactions?${params.toString()}`);
  },
  exportHomeCare: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.status) params.append('status', filters.status);
    return api.get(`/admin/export/home-care?${params.toString()}`);
  },
  
  // Geographic Map
  getMapProfessionals: (specialty) => api.get(`/admin/map/professionals${specialty ? `?specialty=${specialty}` : ''}`),
  getMapFacilities: (facilityType) => api.get(`/admin/map/facilities${facilityType ? `?facility_type=${facilityType}` : ''}`),
  getMapCoverage: () => api.get('/admin/map/coverage'),
  
  // Alerts
  getAlerts: () => api.get('/admin/alerts'),
  
  // System Health
  getHealthMetrics: () => api.get('/admin/health/metrics'),
  getHealthHistory: (period = '24h') => api.get(`/admin/health/history?period=${period}`),
  
  // Push Notifications
  subscribePush: (subscription) => api.post('/admin/notifications/subscribe', subscription),
  unsubscribePush: () => api.delete('/admin/notifications/unsubscribe'),
  getNotificationStatus: () => api.get('/admin/notifications/status'),
  sendTestNotification: () => api.post('/admin/notifications/send-test'),
  getNotificationHistory: (params) => api.get('/admin/notifications/history', { params }),
};

// Specialties API
export const specialtiesAPI = {
  getAll: () => api.get('/specialties'),
};

// Reviews API
export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  getProfessionalReviews: (professionalId) => api.get(`/reviews/professional/${professionalId}`),
  getMyPending: () => api.get('/reviews/my-pending'),
};

// Video Calls API (Jitsi)
export const videoAPI = {
  createRoom: (appointmentId) => api.post('/video/create-room', { appointment_id: appointmentId }),
  getRoom: (appointmentId) => api.get(`/video/room/${appointmentId}`),
  endCall: (appointmentId) => api.post(`/video/end-call/${appointmentId}`),
};

// Chat API
export const chatAPI = {
  getConversations: () => api.get('/chat/conversations'),
  getConversation: (id) => api.get(`/chat/conversations/${id}`),
  createConversation: (professionalId, appointmentId = null) => 
    api.post(`/chat/conversations?professional_id=${professionalId}${appointmentId ? `&appointment_id=${appointmentId}` : ''}`),
  getMessages: (conversationId, limit = 50) => 
    api.get(`/chat/conversations/${conversationId}/messages?limit=${limit}`),
  sendMessage: (conversationId, content) => 
    api.post('/chat/messages', { conversation_id: conversationId, content }),
};

// Medical Documents API
export const medicalDocumentsAPI = {
  getAll: (category = null, familyMemberId = null) => {
    const params = {};
    if (category) params.category = category;
    if (familyMemberId) params.family_member_id = familyMemberId;
    return api.get('/medical-documents', { params });
  },
  getAllWithFamily: (category = null) => 
    api.get(`/medical-documents/all/with-family${category ? `?category=${category}` : ''}`),
  getOne: (id) => api.get(`/medical-documents/${id}`),
  upload: (formData, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/medical-documents/upload${queryParams ? `?${queryParams}` : ''}`;
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  update: (id, data) => api.put(`/medical-documents/${id}`, null, { params: data }),
  delete: (id) => api.delete(`/medical-documents/${id}`),
  share: (id, professionalId, expiresInHours = 48) => 
    api.post(`/medical-documents/${id}/share`, { professional_id: professionalId, expires_in_hours: expiresInHours }),
  revokeAccess: (id, professionalId) => 
    api.post(`/medical-documents/${id}/revoke/${professionalId}`),
  getAccessHistory: (id) => api.get(`/medical-documents/${id}/access-history`),
  exportPDF: () => api.get('/medical-records/export-pdf', { responseType: 'blob' }),
  // Medical Profile (summary) - for patient
  getProfile: () => api.get('/medical-profile'),
  updateProfile: (data) => api.put('/medical-profile', null, { params: data }),
  // Health Timeline - for patient
  getTimeline: (familyMemberId = null) => {
    const params = familyMemberId ? { family_member_id: familyMemberId } : {};
    return api.get('/health-timeline', { params });
  },
  // Export PDF
  exportProfilePDF: () => api.get('/medical-profile/export-pdf', { responseType: 'blob' }),
  // Secure sharing
  createShareLink: (expiresHours = 48) => api.post(`/medical-profile/share?expires_hours=${expiresHours}`),
  getMyShares: () => api.get('/medical-profile/my-shares'),
  revokeShare: (shareId) => api.delete(`/medical-profile/share/${shareId}`),
  getSharedProfile: (token) => api.get(`/shared/medical/${token}`),
  getSharedProfilePDF: (token) => api.get(`/shared/medical/${token}/pdf`, { responseType: 'blob' }),
  // Professional access to patient records
  getPatientProfile: (patientId) => api.get(`/patient/${patientId}/medical-profile`),
  updatePatientProfile: (patientId, data) => api.put(`/patient/${patientId}/medical-profile`, null, { params: data }),
  getPatientTimeline: (patientId) => api.get(`/patient/${patientId}/health-timeline`),
  addPatientNote: (patientId, data) => {
    const params = new URLSearchParams(data).toString();
    return api.post(`/patient/${patientId}/add-note?${params}`);
  }
};

// AI Assistant API
export const aiAssistantAPI = {
  chat: (message, sessionId = null) => 
    api.post('/ai-assistant/chat', { message, session_id: sessionId }),
  chatWithFile: (message, file, sessionId = null) => {
    const formData = new FormData();
    formData.append('message', message);
    formData.append('file', file);
    if (sessionId) formData.append('session_id', sessionId);
    return api.post('/ai-assistant/chat-with-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  guestChat: (message, sessionId = null) => 
    api.post('/ai-assistant/guest-chat', { message, session_id: sessionId }),
  getHistory: (sessionId = null) => 
    api.get(`/ai-assistant/history${sessionId ? `?session_id=${sessionId}` : ''}`),
  getSessions: () => api.get('/ai-assistant/sessions'),
  deleteSession: (sessionId) => api.delete(`/ai-assistant/session/${sessionId}`),
};

// Pharmacy API
export const pharmacyAPI = {
  // Search
  search: (params = {}) => api.get('/pharmacies', { params }),
  getOne: (id) => api.get(`/pharmacies/${id}`),
  
  // Pharmacy profile (for pharmacy users)
  getProfile: () => api.get('/pharmacies/profile'),
  updateProfile: (data) => api.put('/pharmacies/profile', data),
  
  // Prescription orders - Patient
  createOrder: (pharmacyId, file, notes = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (notes) formData.append('notes', notes);
    return api.post(`/pharmacies/orders?pharmacy_id=${pharmacyId}${notes ? `&notes=${notes}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPatientOrders: (status = null) => 
    api.get(`/pharmacies/orders/patient${status ? `?status=${status}` : ''}`),
  
  // Prescription orders - Pharmacy
  getPharmacyOrders: (status = null) => 
    api.get(`/pharmacies/orders/pharmacy${status ? `?status=${status}` : ''}`),
  getOrderDetail: (orderId) => api.get(`/pharmacies/orders/${orderId}`),
  updateOrderStatus: (orderId, data) => api.put(`/pharmacies/orders/${orderId}/status`, data),
  completePayment: (orderId) => api.put(`/pharmacies/orders/${orderId}/complete-payment`),
  cancelOrder: (orderId) => api.delete(`/pharmacies/orders/${orderId}`),
  
  // Reviews
  createReview: (pharmacyId, rating, comment = null) => 
    api.post(`/pharmacies/${pharmacyId}/reviews?rating=${rating}${comment ? `&comment=${encodeURIComponent(comment)}` : ''}`),
  
  // Admin
  getPendingPharmacies: () => api.get('/pharmacies/admin/pending'),
  validatePharmacy: (pharmacyId, approved) => 
    api.put(`/pharmacies/admin/${pharmacyId}/validate?approved=${approved}`),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

// Medical Reminders API
export const remindersAPI = {
  getAll: (familyMemberId = null, includeCompleted = false) => {
    const params = new URLSearchParams();
    if (familyMemberId) params.append('family_member_id', familyMemberId);
    if (includeCompleted) params.append('include_completed', 'true');
    const queryString = params.toString();
    return api.get(`/reminders${queryString ? `?${queryString}` : ''}`);
  },
  getUpcoming: (days = 7) => api.get(`/reminders/upcoming?days=${days}`),
  getTypes: () => api.get('/reminders/types'),
  getOne: (id) => api.get(`/reminders/${id}`),
  create: (data) => api.post('/reminders', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  delete: (id) => api.delete(`/reminders/${id}`),
  complete: (id) => api.post(`/reminders/${id}/complete`),
  testNotification: (id) => api.post(`/reminders/test-notification/${id}`),
};

// Facility Profile API
export const facilityProfileAPI = {
  getMyProfile: () => api.get('/facility-profile/my-profile'),
  updateProfile: (data) => api.put('/facility-profile/update', null, { params: data }),
  uploadPhoto: (type, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/facility-profile/upload-photo/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addGalleryPhoto: (file, caption = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = caption ? `?caption=${encodeURIComponent(caption)}` : '';
    return api.post(`/facility-profile/gallery/add${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeGalleryPhoto: (photoId) => api.delete(`/facility-profile/gallery/${photoId}`),
  setYoutubeVideo: (url) => api.post(`/facility-profile/video?youtube_url=${encodeURIComponent(url)}`),
  removeYoutubeVideo: () => api.delete('/facility-profile/video'),
  updateSpecialties: (specialties) => api.post('/facility-profile/specialties', specialties),
  updateServices: (services) => api.post('/facility-profile/services', services),
  updateHours: (hours) => api.post('/facility-profile/hours', hours),
  inviteProfessional: (email) => api.post(`/facility-profile/professionals/invite?professional_email=${encodeURIComponent(email)}`),
  getInvitations: () => api.get('/facility-profile/professionals/invitations'),
  getProfessionals: () => api.get('/facility-profile/professionals'),
  removeProfessional: (associationId) => api.delete(`/facility-profile/professionals/${associationId}`),
  // For professionals
  getMyInvitations: () => api.get('/facility-profile/my-invitations'),
  respondToInvitation: (invitationId, accept) => 
    api.post(`/facility-profile/invitations/${invitationId}/respond?accept=${accept}`),
};

// Chariow Payment API
export const chariowAPI = {
  createPending: (data) => api.post('/chariow/create-pending', data),
  checkStatus: (reference) => api.get(`/chariow/status/${reference}`),
};

// Credits API
export const creditsAPI = {
  getBalance: () => api.get('/credits/balance'),
  getPacks: () => api.get('/credits/packs'),
  getPlans: () => api.get('/credits/plans'),
  purchase: (data) => api.post('/credits/purchase', data),
  checkPurchaseStatus: (reference) => api.get(`/credits/purchase/status/${reference}`),
  subscribe: (data) => api.post('/credits/subscribe', data),
  getTransactions: (limit = 50) => api.get(`/credits/transactions?limit=${limit}`),
};

export default api;
